import apiClient from "@services/api/apiClient.jsx";

/**
 * 페이지 프리셋 관련 API 서비스
 */
const presetApi = {
    /**
     * 프리셋 목록 조회
     * @param {number} page 페이지 번호
     * @param {number} size 페이지 크기
     * @returns {Promise<Object>} 프리셋 목록 (페이징 정보 포함)
     */
    getPresets: async (page = 0, size = 20) => {
        try {
            const response = await apiClient.get('/presets', {
                params: { page, size }
            });
            return response.data;
        } catch (error) {
            console.error('프리셋 목록 조회 실패:', error);
            throw error;
        }
    },

    /**
     * 프리셋 상세 조회
     * @param {number} id 프리셋 ID
     * @returns {Promise<Object>} 프리셋 상세 정보
     */
    getPreset: async (id) => {
        try {
            // 캐시 방지를 위한 타임스탬프 추가
            const response = await apiClient.get(`/presets/${id}`);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${id} 조회 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 생성
     * @param {Object} presetData 프리셋 데이터
     * @returns {Promise<Object>} 생성된 프리셋 정보
     */
    createPreset: async (presetData) => {
        try {
            const response = await apiClient.post('/presets', presetData);
            return response.data;
        } catch (error) {
            console.error('프리셋 생성 실패:', error);
            throw error;
        }
    },

    /**
     * 프리셋 수정
     * @param {number} id 프리셋 ID
     * @param {Object} presetData 프리셋 데이터
     * @returns {Promise<Object>} 수정된 프리셋 정보
     */
    updatePreset: async (id, presetData) => {
        try {
            console.log('=== updatePreset 호출 ===');
            console.log('presetId:', id);
            console.log('presetData:', presetData);
            const response = await apiClient.put(`/presets/${id}`, presetData);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${id} 수정 실패:`, error);
            console.error('에러 응답:', error.response?.data);
            console.error('에러 상태:', error.response?.status);
            console.error('에러 메시지:', error.response?.data?.message || error.message);

            // 백엔드에서 반환한 에러 메시지 추출
            const errorMessage = error.response?.data?.message || error.message || '프리셋 수정 중 오류가 발생했습니다.';

            // 에러 객체에 메시지 추가
            error.userMessage = errorMessage;
            throw error;
        }
    },

    /**
     * 프리셋 삭제
     * @param {number} id 프리셋 ID
     * @returns {Promise<void>}
     */
    deletePreset: async (id) => {
        try {
            await apiClient.delete(`/presets/${id}`);
        } catch (error) {
            console.error(`프리셋 ID ${id} 삭제 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 배포
     * @param {number} id 프리셋 ID
     * @returns {Promise<Object>} 배포된 프리셋 정보
     */
    deployPreset: async (id) => {
        try {
            const response = await apiClient.post(`/presets/${id}/deploy`);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${id} 배포 실패:`, error);
            throw error;
        }
    },

    /**
     * 테마로부터 프리셋 생성
     * @param {number} themeId 테마 ID
     * @param {number} branchId 지점 ID
     * @param {string} presetName 프리셋 이름
     * @returns {Promise<Object>} 생성된 프리셋 정보
     */
    createPresetFromTheme: async (themeId, branchId, presetName) => {
        try {
            const response = await apiClient.post('/presets/from-theme', null, {
                params: { themeId, branchId, presetName }
            });
            return response.data;
        } catch (error) {
            console.error('테마에서 프리셋 생성 실패:', error);
            throw error;
        }
    },

    /**
     * 지점별 활성 프리셋 조회
     * @param {number} branchId 지점 ID
     * @returns {Promise<Array>} 활성 프리셋 목록
     */
    getActivePresetsByBranch: async (branchId) => {
        try {
            const response = await apiClient.get(`/presets/branch/${branchId}/active`);
            return response.data;
        } catch (error) {
            console.error(`지점 ID ${branchId} 활성 프리셋 조회 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 유효성 검증
     * @param {number} id 프리셋 ID
     * @returns {Promise<boolean>} 유효성 검증 결과
     */
    validatePreset: async (id) => {
        try {
            const response = await apiClient.get(`/presets/${id}/validate`);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${id} 유효성 검증 실패:`, error);
            throw error;
        }
    },

    // Page Design API methods
    /**
     * 프리셋 시작 페이지 조회
     * @param {number} presetId 프리셋 ID
     * @returns {Promise<Object>} 시작 페이지 정보
     */
    getStartPage: async (presetId) => {
        try {
            const response = await apiClient.get(`/presets/pageDesign/${presetId}/startPage`);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${presetId} 시작 페이지 조회 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 시작 페이지 저장
     * @param {number} presetId 프리셋 ID
     * @param {Object} startPageData 시작 페이지 데이터
     * @returns {Promise<Object>} 저장된 시작 페이지 정보
     */
    saveStartPage: async (presetId, startPageData) => {
        try {
            const response = await apiClient.post(`/presets/pageDesign/${presetId}/startPage`, startPageData);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${presetId} 시작 페이지 저장 실패:`, error);
            throw error;
        }
    },

    /**
     * PresetPageMaster isActive 값 일괄 업데이트
     * @param {number} presetId 프리셋 ID
     * @param {Array} updateRequests 업데이트 요청 목록
     * @returns {Promise<Array>} 업데이트된 PresetPageMaster 목록
     */
    updatePageMasterToggles: async (presetId, updateRequests) => {
        try {
            const response = await apiClient.put(`/presets/${presetId}/pageMasters/bulk-update`, updateRequests);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${presetId} PresetPageMaster 업데이트 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 앵글 선택 페이지 조회
     * @param {number} presetId 프리셋 ID
     * @returns {Promise<Object>} 앵글 선택 페이지 정보
     */
    getAngleSelectPage: async (presetId) => {
        try {
            const response = await apiClient.get(`/presets/pageDesign/${presetId}/angleSelectPage`);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${presetId} 앵글 선택 페이지 조회 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 앵글 선택 페이지 저장
     * @param {number} presetId 프리셋 ID
     * @param {Object} angleSelectPageData 앵글 선택 페이지 데이터
     * @returns {Promise<Object>} 저장된 앵글 선택 페이지 정보
     */
    saveAngleSelectPage: async (presetId, angleSelectPageData) => {
        try {
            const response = await apiClient.post(`/presets/pageDesign/${presetId}/angleSelectPage`, angleSelectPageData);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${presetId} 앵글 선택 페이지 저장 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 레이아웃 선택 페이지 조회
     * @param {number} presetId 프리셋 ID
     * @returns {Promise<Object>} 레이아웃 선택 페이지 정보
     */
    getLayoutSelectPage: async (presetId) => {
        try {
            const response = await apiClient.get(`/presets/pageDesign/${presetId}/layoutSelectPage`);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${presetId} 레이아웃 선택 페이지 조회 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 레이아웃 선택 페이지 저장
     * @param {number} presetId 프리셋 ID
     * @param {Object} layoutSelectPageData 레이아웃 선택 페이지 데이터
     * @returns {Promise<Object>} 저장된 레이아웃 선택 페이지 정보
     */
    saveLayoutSelectPage: async (presetId, layoutSelectPageData) => {
        try {
            const response = await apiClient.post(`/presets/pageDesign/${presetId}/layoutSelectPage`, layoutSelectPageData);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${presetId} 레이아웃 선택 페이지 저장 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 수량 선택 페이지 조회
     * @param {number} presetId 프리셋 ID
     * @returns {Promise<Object>} 수량 선택 페이지 정보
     */
    getAmountSelectPage: async (presetId) => {
        try {
            const response = await apiClient.get(`/presets/pageDesign/${presetId}/amountSelectPage`);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${presetId} 수량 선택 페이지 조회 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 수량 선택 페이지 저장
     * @param {number} presetId 프리셋 ID
     * @param {Object} amountSelectPageData 수량 선택 페이지 데이터
     * @returns {Promise<Object>} 저장된 수량 선택 페이지 정보
     */
    saveAmountSelectPage: async (presetId, amountSelectPageData) => {
        try {
            const response = await apiClient.post(`/presets/pageDesign/${presetId}/amountSelectPage`, amountSelectPageData);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${presetId} 수량 선택 페이지 저장 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 결제 페이지 조회
     * @param {number} presetId 프리셋 ID
     * @returns {Promise<Object>} 결제 페이지 정보
     */
    getPaymentPage: async (presetId) => {
        try {
            const response = await apiClient.get(`/presets/pageDesign/${presetId}/paymentPage`);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${presetId} 결제 페이지 조회 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 결제 페이지 저장
     * @param {number} presetId 프리셋 ID
     * @param {Object} paymentPageData 결제 페이지 데이터
     * @returns {Promise<Object>} 저장된 결제 페이지 정보
     */
    savePaymentPage: async (presetId, paymentPageData) => {
        try {
            const response = await apiClient.post(`/presets/pageDesign/${presetId}/paymentPage`, paymentPageData);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${presetId} 결제 페이지 저장 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 결제 로딩 페이지 조회
     * @param {number} presetId 프리셋 ID
     * @returns {Promise<Object>} 결제 로딩 페이지 정보
     */
    getPaymentLoadingPage: async (presetId) => {
        try {
            const response = await apiClient.get(`/presets/pageDesign/${presetId}/paymentLoadingPage`);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${presetId} 결제 로딩 페이지 조회 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 결제 로딩 페이지 저장
     * @param {number} presetId 프리셋 ID
     * @param {Object} paymentLoadingPageData 결제 로딩 페이지 데이터
     * @returns {Promise<Object>} 저장된 결제 로딩 페이지 정보
     */
    savePaymentLoadingPage: async (presetId, paymentLoadingPageData) => {
        try {
            const response = await apiClient.post(`/presets/pageDesign/${presetId}/paymentLoadingPage`, paymentLoadingPageData);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${presetId} 결제 로딩 페이지 저장 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 촬영 대기 페이지 조회
     * @param {number} presetId 프리셋 ID
     * @returns {Promise<Object>} 촬영 대기 페이지 정보
     */
    getCaptureStandbyPage: async (presetId) => {
        try {
            const response = await apiClient.get(`/presets/pageDesign/${presetId}/captureStandbyPage`);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${presetId} 촬영 대기 페이지 조회 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 촬영 대기 페이지 저장
     * @param {number} presetId 프리셋 ID
     * @param {Object} captureStandbyPageData 촬영 대기 페이지 데이터
     * @returns {Promise<Object>} 저장된 촬영 대기 페이지 정보
     */
    saveCaptureStandbyPage: async (presetId, captureStandbyPageData) => {
        try {
            const response = await apiClient.post(`/presets/pageDesign/${presetId}/captureStandbyPage`, captureStandbyPageData);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${presetId} 촬영 대기 페이지 저장 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 촬영 페이지 조회
     * @param {number} presetId 프리셋 ID
     * @returns {Promise<Object>} 촬영 페이지 정보
     */
    getCapturePage: async (presetId) => {
        try {
            const response = await apiClient.get(`/presets/pageDesign/${presetId}/capturePage`);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${presetId} 촬영 페이지 조회 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 촬영 페이지 저장
     * @param {number} presetId 프리셋 ID
     * @param {Object} capturePageData 촬영 페이지 데이터
     * @returns {Promise<Object>} 저장된 촬영 페이지 정보
     */
    saveCapturePage: async (presetId, capturePageData) => {
        try {
            const response = await apiClient.post(`/presets/pageDesign/${presetId}/capturePage`, capturePageData);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${presetId} 촬영 페이지 저장 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 촬영 후 로딩 페이지 조회
     * @param {number} presetId 프리셋 ID
     * @returns {Promise<Object>} 촬영 후 로딩 페이지 정보
     */
    getPostCaptureLoadingPage: async (presetId) => {
        try {
            const response = await apiClient.get(`/presets/pageDesign/${presetId}/postCaptureLoadingPage`);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${presetId} 촬영 후 로딩 페이지 조회 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 촬영 후 로딩 페이지 저장
     * @param {number} presetId 프리셋 ID
     * @param {Object} postCaptureLoadingPageData 촬영 후 로딩 페이지 데이터
     * @returns {Promise<Object>} 저장된 촬영 후 로딩 페이지 정보
     */
    savePostCaptureLoadingPage: async (presetId, postCaptureLoadingPageData) => {
        try {
            const response = await apiClient.post(`/presets/pageDesign/${presetId}/postCaptureLoadingPage`, postCaptureLoadingPageData);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${presetId} 촬영 후 로딩 페이지 저장 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 사진 선택 페이지 조회
     * @param {number} presetId 프리셋 ID
     * @returns {Promise<Object>} 사진 선택 페이지 정보
     */
    getPhotoSelectionPage: async (presetId) => {
        try {
            const response = await apiClient.get(`/presets/pageDesign/${presetId}/photoSelectionPage`);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${presetId} 사진 선택 페이지 조회 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 사진 선택 페이지 저장
     * @param {number} presetId 프리셋 ID
     * @param {Object} photoSelectionPageData 사진 선택 페이지 데이터
     * @returns {Promise<Object>} 저장된 사진 선택 페이지 정보
     */
    savePhotoSelectionPage: async (presetId, photoSelectionPageData) => {
        try {
            const response = await apiClient.post(`/presets/pageDesign/${presetId}/photoSelectionPage`, photoSelectionPageData);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${presetId} 사진 선택 페이지 저장 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 인쇄 대기 페이지 조회
     * @param {number} presetId 프리셋 ID
     * @returns {Promise<Object>} 인쇄 대기 페이지 정보
     */
    getPrintWaitingPage: async (presetId) => {
        try {
            const response = await apiClient.get(`/presets/pageDesign/${presetId}/printWaitingPage`);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${presetId} 인쇄 대기 페이지 조회 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 인쇄 대기 페이지 저장
     * @param {number} presetId 프리셋 ID
     * @param {Object} printWaitingPageData 인쇄 대기 페이지 데이터
     * @returns {Promise<Object>} 저장된 인쇄 대기 페이지 정보
     */
    savePrintWaitingPage: async (presetId, printWaitingPageData) => {
        try {
            const response = await apiClient.post(`/presets/pageDesign/${presetId}/printWaitingPage`, printWaitingPageData);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${presetId} 인쇄 대기 페이지 저장 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 QR 뷰어 페이지 조회
     * @param {number} presetId 프리셋 ID
     * @returns {Promise<Object>} QR 뷰어 페이지 정보
     */
    getQrViewerPage: async (presetId) => {
        try {
            const response = await apiClient.get(`/presets/pageDesign/${presetId}/qrViewerPage`);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${presetId} QR 뷰어 페이지 조회 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 QR 뷰어 페이지 저장
     * @param {number} presetId 프리셋 ID
     * @param {Object} qrViewerPageData QR 뷰어 페이지 데이터
     * @returns {Promise<Object>} 저장된 QR 뷰어 페이지 정보
     */
    saveQrViewerPage: async (presetId, qrViewerPageData) => {
        try {
            const response = await apiClient.post(`/presets/pageDesign/${presetId}/qrViewerPage`, qrViewerPageData);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${presetId} QR 뷰어 페이지 저장 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 모달 페이지 조회
     * @param {number} presetId 프리셋 ID
     * @returns {Promise<Object>} 모달 페이지 정보
     */
    getModalPage: async (presetId) => {
        try {
            const response = await apiClient.get(`/presets/pageDesign/${presetId}/modalPage`);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${presetId} 모달 페이지 조회 실패:`, error);
            throw error;
        }
    },

    /**
     * 프리셋 모달 페이지 저장
     * @param {number} presetId 프리셋 ID
     * @param {Object} modalPageData 모달 페이지 데이터
     * @returns {Promise<Object>} 저장된 모달 페이지 정보
     */
    saveModalPage: async (presetId, modalPageData) => {
        try {
            const response = await apiClient.post(`/presets/pageDesign/${presetId}/modalPage`, modalPageData);
            return response.data;
        } catch (error) {
            console.error(`프리셋 ID ${presetId} 모달 페이지 저장 실패:`, error);
            throw error;
        }
    }
};

export default presetApi;
